import { createColumnHelper } from '@tanstack/react-table';

import { CopyableLink } from '@/components/copyable-link';
import { PeopleBadgeList } from '@/routes/_layout/scheduling/student-groups/-components/people-badge-list';
import { EditSessionModal } from '@/routes/_layout/sessions/-components/edit-session-modal';
import {
  formatDateWithDayOfWeek,
  formatInstant,
  formatTimeRange,
  FrequentTimeZones,
  safeInstantFromString,
} from '@airreading/common';
import { AdminSession } from '@airreading/common/data';
import { SessionTeacherDisplay } from '@/routes/_layout/sessions/-components/session-teacher-display';
import { OriginalTeacherDisplay } from '@/routes/_layout/sessions/-components/original-teacher-display';
import { MeetingStatusBadge } from '@/components/session/meeting-status-badge';

const adminCol = createColumnHelper<AdminSession>();

function resolveLessonLink(lesson: AdminSession['lesson']) {
  if (!lesson) return null;
  return lesson.lessonLinkKTo2 || lesson.lessonLink3To5 || lesson.lessonLink6To8 || null;
}

export const classSessionsDrawerColumns = (displayTimeZone: string) => [
  adminCol.accessor((row) => row, {
    id: 'editSession',
    header: '',
    cell: ({ getValue }) => <EditSessionModal session={getValue()} />,
  }),

  adminCol.accessor('startInstant', {
    id: 'date',
    header: 'Date (CT)',
    cell: ({ getValue }) => {
      const startInstant = getValue();
      return formatDateWithDayOfWeek(startInstant, displayTimeZone);
    },
  }),

  adminCol.accessor('startInstant', {
    id: 'time',
    header: 'Time (CT)',
    cell: ({ getValue, row }) => {
      const startInstant = getValue();
      const endInstant = row.original.endInstant;
      return formatTimeRange(startInstant, endInstant, FrequentTimeZones.UsCentral);
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'meetingStatus',
    header: 'Meeting status',
    cell: ({ getValue }) => {
      const { meeting } = getValue();
      return <>{meeting?.status ? <MeetingStatusBadge status={meeting.status} /> : '-'}</>;
    },
  }),

  adminCol.accessor((row) => resolveLessonLink(row.lesson), {
    id: 'lesson',
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const link = getValue();
      const lesson = row.original.lesson;
      if (!lesson) return '—';
      if (!link) return <div className="flex items-center gap-2">{lesson.name}</div>;

      return (
        <div className="flex items-center gap-2">
          <CopyableLink
            externalHref={link}
            className="text-blue-600 hover:text-blue-800 underline"
            showExternalHrefIcon={false}
          >
            {lesson.name}
          </CopyableLink>
        </div>
      );
    },
  }),

  adminCol.accessor('students', {
    id: 'students',
    header: 'Students',
    cell: ({ getValue }) => {
      return <PeopleBadgeList people={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'sessionTeacher',
    header: 'Session Teacher',
    cell: ({ getValue }) => {
      return <SessionTeacherDisplay session={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'originalTeacher',
    header: 'Original Teacher',
    cell: ({ getValue }) => {
      return <OriginalTeacherDisplay session={getValue()} />;
    },
  }),

  adminCol.accessor((row) => row, {
    id: 'createdAt',
    header: 'Created time (CT)',
    cell: ({ getValue }) => {
      const { createdAt } = getValue();
      const instant = safeInstantFromString(String(createdAt));
      const { formattedDateTime } = formatInstant(instant, FrequentTimeZones.UsCentral);
      return <div>{formattedDateTime}</div>;
    },
  }),
];
